import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import { StatisticsService } from '../StatisticsService';
import { IStatisticOffer } from '../dto/iStatisticOffer';
import { Pagination } from '../../../shared/components/Pagination';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { StatisticsOffersFilters } from './StatisticsOffersFilters';
import { ErrorToast } from '../../../utils/toasters';
import _ from 'lodash';
import { TableSkeletonRows } from '../../../shared/components/TableSkeleton';

export function StatisticsOffers() {
    const [statistics, setStatistics] = useState<IStatisticOffer[]>([]);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
        orderCriteria: 'userId',
        orderType: 'ASC' as 'ASC' | 'DESC',
        minPriceFrom: undefined,
        minPriceTo: undefined,
        maxPriceTo: undefined,
        maxPriceFrom: undefined,
        averagePriceFrom: undefined,
        averagePriceTo: undefined,
    });

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isCurrent = true;
        setIsLoading(true);

        StatisticsService.getStatisticsOffers(pagination)
            .then(data => {
                if (!isCurrent) return;
                setStatistics(data.data);
                setTotalPages(Math.ceil(data.count / pagination.perPage));
            })
            .catch(err => {
                if (isCurrent) ErrorToast(err);
            })
            .finally(() => {
                if (isCurrent) setIsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [pagination]);

    const handlePages = (updatePage: number) => {
        setPagination({ ...pagination, page: updatePage });
    };

    const handleSort = (key: keyof IStatisticOffer) => {
        setPagination(prev => {
            const isSameColumn = prev.orderCriteria === key;
            return {
                ...prev,
                orderCriteria: key,
                orderType: isSameColumn && prev.orderType === 'ASC' ? 'DESC' : 'ASC',
            };
        });
    };

    function handleClick() {
        setPagination(prev => {
            return { ...prev, page: 1 };
        });
    }

    const updateFilter = useMemo(
        () =>
            _.debounce((name: string, value: string) => {
                setPagination(prev => ({ ...prev, [name]: value ? +value : undefined, page: 1 }));
            }, 300),
        []
    );

    useEffect(() => () => updateFilter.cancel(), [updateFilter]);

    function handleChange(evt: any) {
        updateFilter(evt.target.name, evt.target.value);
    }

    return (
        <>
            <Card
                className="mb-5 p-4"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick();
                }}
            >
                <StatisticsOffersFilters handleChanges={handleChange} />
            </Card>
            <Card className="mb-5 p-4">
                <Table hover responsive>
                    <thead>
                        <tr>
                            <th
                                className={`cursor-pointer ${
                                    pagination.orderCriteria === 'userId' ? 'text-primary' : 'disabled'
                                }`}
                                onClick={() => handleSort('userId')}
                            >
                                Influencer ID{pagination.orderType === 'ASC' ? '↑' : '↓'}
                            </th>
                            <th>Influencer name</th>
                            <th
                                className={`cursor-pointer ${
                                    pagination.orderCriteria === 'minPrice' ? 'text-primary' : 'disabled'
                                }`}
                                onClick={() => handleSort('minPrice')}
                            >
                                Lowest price{pagination.orderType === 'ASC' ? '↑' : '↓'}
                            </th>
                            <th
                                className={`cursor-pointer ${
                                    pagination.orderCriteria === 'maxPrice' ? 'text-primary' : 'disabled'
                                }`}
                                onClick={() => handleSort('maxPrice')}
                            >
                                Highest price{pagination.orderType === 'ASC' ? '↑' : '↓'}
                            </th>
                            <th
                                className={`cursor-pointer ${
                                    pagination.orderCriteria === 'averagePrice' ? 'text-primary' : 'disabled'
                                }`}
                                onClick={() => handleSort('averagePrice')}
                            >
                                AVG price{pagination.orderType === 'ASC' ? '↑' : '↓'}
                            </th>
                            <th
                                className={`cursor-pointer ${
                                    pagination.orderCriteria === 'totalOffers' ? 'text-primary' : 'disabled'
                                }`}
                                onClick={() => handleSort('totalOffers')}
                            >
                                Total accepted{pagination.orderType === 'ASC' ? '↑' : '↓'}
                            </th>
                        </tr>
                    </thead>
                    <tbody aria-busy={isLoading}>
                        {isLoading ? (
                            <TableSkeletonRows columns={6} rows={pagination.perPage} />
                        ) : (
                            statistics.map(e => (
                                <tr key={e.userId}>
                                    <td>
                                        {e.userId}
                                        <ImpersonateEye id={e.userId ?? 0} />
                                    </td>
                                    <td className="text-break">
                                        {e.displayName && (
                                            <>
                                                {e.displayName}
                                                <br />
                                            </>
                                        )}
                                        {e.firstName ?? '-'} {e.lastName}
                                    </td>
                                    <td>{priceSplitter(e.minPrice)}€</td>
                                    <td>{priceSplitter(e.maxPrice)}€</td>
                                    <td>{priceSplitter(e.averagePrice)}€</td>
                                    <td>{e.totalOffers}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
                <Pagination
                    page={pagination.page}
                    totalPages={totalPages}
                    handlePagination={handlePages}
                    disabled={isLoading}
                />
            </Card>
        </>
    );
}
